import React, { useEffect, useRef, useState } from 'react';
import { updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { auth } from '../firebase/config';
import '../styles/test-system.css';
import './ChangePasswordModal.css';

const ChangePasswordModal = ({ isOpen, onClose, onSuccess }) => {
    /* The Escape handler is bound once per open; the ref keeps it pointing at the
       current `handleClose` without re-binding the listener on every keystroke. */
    const handleCloseRef = useRef(() => {});
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validation
        if (!currentPassword || !newPassword || !confirmPassword) {
            setError('Please fill in all fields');
            return;
        }

        if (newPassword !== confirmPassword) {
            setError('New passwords do not match');
            return;
        }

        if (newPassword.length < 6) {
            setError('New password must be at least 6 characters long');
            return;
        }

        setLoading(true);

        try {
            const user = auth.currentUser;
            
            if (!user) {
                setError('User not authenticated');
                return;
            }

            // Re-authenticate user with current password
            const credential = EmailAuthProvider.credential(user.email, currentPassword);
            await reauthenticateWithCredential(user, credential);

            // Update password
            await updatePassword(user, newPassword);

            // Success
            onSuccess('Your password is changed.');
            handleClose();
        } catch (err) {
            console.error('Error changing password:', err);
            
            switch (err.code) {
                case 'auth/wrong-password':
                    setError('Current password is incorrect');
                    break;
                case 'auth/weak-password':
                    setError('New password is too weak');
                    break;
                case 'auth/requires-recent-login':
                    setError('Please log out and log back in before changing your password');
                    break;
                default:
                    setError(err.message || 'Failed to change password. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setError('');
        onClose();
    };

    /* A dialog closes on Escape. This one only closed on the backdrop, the ×
       and Cancel, which a keyboard reader cannot find without tabbing to them. */
    useEffect(() => {
        if (!isOpen) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape') handleCloseRef.current();
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    handleCloseRef.current = handleClose;

    if (!isOpen) return null;

    return (
        <div className="cpm-overlay" onClick={handleClose}>
            <div
                className="cpm-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="cpm-title"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="cpm-head">
                    <h2 className="cpm-title" id="cpm-title">Change password</h2>
                    <button className="cpm-close" onClick={handleClose} aria-label="Close">
                        ×
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="cpm-form">
                    {error && (
                        <p className="ts-note ts-note--danger cpm-error" role="alert">
                            <span>{error}</span>
                        </p>
                    )}

                    <div className="cpm-field">
                        <label className="cpm-label" htmlFor="currentPassword">Current password</label>
                        <input
                            className="cpm-input"
                            type="password"
                            id="currentPassword"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            disabled={loading}
                            required
                        />
                    </div>

                    <div className="cpm-field">
                        <label className="cpm-label" htmlFor="newPassword">New password</label>
                        <input
                            className="cpm-input"
                            type="password"
                            id="newPassword"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            disabled={loading}
                            required
                            minLength="6"
                        />
                        <p className="cpm-hint">At least 6 characters.</p>
                    </div>

                    <div className="cpm-field">
                        <label className="cpm-label" htmlFor="confirmPassword">Confirm new password</label>
                        <input
                            className="cpm-input"
                            type="password"
                            id="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            disabled={loading}
                            required
                        />
                    </div>

                    <div className="cpm-actions">
                        <button
                            type="button"
                            className="ts-btn"
                            onClick={handleClose}
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="ts-btn ts-btn--primary"
                            disabled={loading}
                        >
                            {loading && <span className="ts-spinner-sm" />}
                            {loading ? 'Changing…' : 'Change password'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ChangePasswordModal; 